/**
 * Turret — the RUN-AND-GUN aim-tracking emplacement (step 4). The genre's signature enemy.
 *
 * A STATIC mounted gun (no gravity — sits exactly where placed, so it works ground / wall /
 * ceiling / floating) that TRACKS the player: it resolves an 8-way aim toward the player (matching
 * the player's own 8-way gun) and, while the player is in range, fires an ENEMY projectile along
 * that aim after a short telegraph (the `_fire` pose). UN-stompable + contact-hurts; only a bullet
 * kills it, and it SOAKS `props.hp` (default 4) via the shared kill seam. Combines the aim-track
 * (d) + hp (e) primitives.
 *
 * Determinism: integer step counter + abs/min/max + explicit-sign ternaries + Math.SQRT2 constant
 * (no sqrt/sin/cos call); the shot emits through ctx.spawnEntityAt's deterministic queue. No
 * random / wall-clock / transcendentals → ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { PROJECTILE_H } from './Projectile';

const SQUASH_SECONDS = 0.4;
const DEFAULT_COOLDOWN = 90;
const DEFAULT_TELEGRAPH = 18;
const DEFAULT_BULLET_SPEED = 320;
const DEFAULT_BULLET_LIFE = 200;
const DEFAULT_RANGE_PX = 900;
const DEFAULT_HP = 4;

export class Turret implements Entity {
  readonly type = EntityType.TURRET;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true;
  alive = true;
  hp: number;
  squashTimer = 0;

  private cooldown: number;
  private telegraph: number;
  private bulletSpeed: number;
  private bulletLife: number;
  private rangeSq: number;
  private sinceFire = 0;
  private firePose = false;

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 52, h = 52) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.cooldown = typeof props?.fireCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.fireCooldownSteps)) : DEFAULT_COOLDOWN;
    this.telegraph = typeof props?.telegraphSteps === 'number'
      ? Math.max(1, Math.floor(props.telegraphSteps)) : DEFAULT_TELEGRAPH;
    this.bulletSpeed = typeof props?.bulletSpeed === 'number' ? props.bulletSpeed : DEFAULT_BULLET_SPEED;
    this.bulletLife = typeof props?.bulletLifeSteps === 'number'
      ? Math.floor(props.bulletLifeSteps) : DEFAULT_BULLET_LIFE;
    const range = typeof props?.rangePx === 'number' ? props.rangePx : DEFAULT_RANGE_PX;
    this.rangeSq = range * range;
    this.hp = typeof props?.hp === 'number' ? Math.max(1, Math.floor(props.hp)) : DEFAULT_HP;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const scx = this.body.x + this.body.w / 2;
    const scy = this.body.y + this.body.h / 2;
    const ax = (ctx.player.body.x + ctx.player.body.w / 2) - scx;
    const ay = (ctx.player.body.y + ctx.player.body.h / 2) - scy;

    if (ax < 0) this.facing = -1; else this.facing = 1; // render flip toward the player

    const inRange = ax * ax + ay * ay <= this.rangeSq;
    if (inRange) {
      this.sinceFire++;
      this.firePose = this.sinceFire >= this.cooldown - this.telegraph;
      if (this.sinceFire >= this.cooldown) { this.fire(ctx, ax, ay); this.sinceFire = 0; this.firePose = false; }
    } else {
      this.sinceFire = 0;
      this.firePose = false;
    }
  }

  private fire(ctx: EntityContext, ax: number, ay: number): void {
    // 8-way quantize toward the player (matches the player's own 8-way gun), normalized to speed.
    const absx = Math.abs(ax), absy = Math.abs(ay);
    const sx = ax < 0 ? -1 : 1;
    const sy = ay < 0 ? -1 : 1;
    let ux: number, uy: number;
    if (absx > absy * 2) { ux = sx; uy = 0; }
    else if (absy > absx * 2) { ux = 0; uy = sy; }
    else { ux = sx; uy = sy; }
    const len = ux !== 0 && uy !== 0 ? Math.SQRT2 : 1; // cardinal → 1, diagonal → √2 (constant, no sqrt call)
    const scx = this.body.x + this.body.w / 2;
    const scy = this.body.y + this.body.h / 2;
    const muzzleCx = scx + ux * (this.body.w * 0.45);
    const muzzleCy = scy + uy * (this.body.h * 0.45);
    ctx.spawnEntityAt(EntityType.PROJECTILE, muzzleCx, muzzleCy + PROJECTILE_H / 2, {
      vx: (ux / len) * this.bulletSpeed, vy: (uy / len) * this.bulletSpeed,
      life: this.bulletLife, owner: 'enemy',
    });
    ctx.playSound('shoot', 0.3, 0.55); // POLISH_01 #1 — turret shot
  }

  onPlayerOverlap(ctx: EntityContext): void { ctx.hurtPlayer(); } // un-stompable emplacement

  damageable(): boolean { return true; }                          // bullet-killable (seam chips hp)
  onHit(ctx: EntityContext): void { ctx.playSound('bump', 0.3, 1.0); } // POLISH_01 #1 — metallic chip clink

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
  }

  spriteKey(): string {
    if (!this.alive) return 'turret_rest';
    return this.firePose ? 'turret_fire' : 'turret_rest';
  }
  visible(): boolean { return this.alive || this.squashTimer > 0; }
}
